import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { AuthApiService } from '../../../core/auth/auth-api.service';
import { decodeJwtClaims, isJwtExpired } from '../../../core/auth/jwt';
import { ThemeService } from '../../../core/services/theme.service';
import { NotificationService } from '../../../services/notification.service';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { PhoneInputComponent } from '../../../shared/components/phone-input/phone-input.component';
import { COUNTRIES } from '../../../shared/data/countries';
import * as AuthActions from '../../../store/auth/auth.actions';
import { selectAuthStatus } from '../../../store/auth/auth.selectors';

type AuthMethod = 'otp' | 'password';
type IdentifierType = 'phone' | 'email';
type OtpStep = 'target' | 'code';

@Component({
    selector: 'app-login-page',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterLink, LucideIconsModule, PhoneInputComponent],
    templateUrl: './login-page.component.html',
    styleUrls: ['./login-page.component.scss']
})
export class LoginPageComponent implements OnInit {
    private fb = inject(FormBuilder);
    private authApi = inject(AuthApiService);
    private store = inject(Store);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private notifications = inject(NotificationService);
    protected themeService = inject(ThemeService);

    private readonly localAuthHeroImage = 'assets/imagery/avif/hero-building.avif?v=20260329';
    authHeroImage = this.localAuthHeroImage;

    private readonly localLogoBlack = 'https://assetsnibni.zohostratus.com/images/logos/nibni-logo-white.avif';
    private readonly localLogoWhite = 'https://assetsnibni.zohostratus.com/images/logos/nibni-logo-white.avif';

    method = signal<AuthMethod>('otp');
    identifier = signal<IdentifierType>('phone');
    otpStep = signal<OtpStep>('target');
    loading = signal(false);
    message = signal<string | null>(null);
    error = signal<string | null>(null);
    showPassword = signal(false);

    otpForm = this.fb.group({
        mobile: this.fb.control({ countryCode: 'EG', phone: '' }, Validators.required),
        email: this.fb.control('', [Validators.email]),
        code: this.fb.control<string>('', [])
    });

    passwordForm = this.fb.group({
        mobile: this.fb.control({ countryCode: 'EG', phone: '' }, Validators.required),
        email: this.fb.control('', [Validators.email]),
        password: this.fb.control('', [Validators.required])
    });

    get logoSrc(): string {
        const isDarkTheme = this.themeService.theme() !== 'white';
        return isDarkTheme ? this.localLogoWhite : this.localLogoBlack;
    }

    get otpButtonLabel(): string {
        if (this.loading()) return this.otpStep() === 'target' ? 'Sending...' : 'Verifying...';
        return this.otpStep() === 'target' ? 'Send code' : 'Verify code';
    }

    setMethod(method: AuthMethod) {
        if (this.method() === method) return;
        this.method.set(method);
        this.resetTransientState();
    }

    setIdentifier(identifier: IdentifierType) {
        if (this.identifier() === identifier) return;
        this.identifier.set(identifier);
        this.resetTransientState();
    }

    private resetTransientState(): void {
        this.message.set(null);
        this.error.set(null);
        this.loading.set(false);
        this.otpStep.set('target');
        this.otpForm.patchValue({ code: '' });
    }

    ngOnInit(): void {
        const qp = this.route.snapshot.queryParamMap;
        const method = qp.get('method');
        const identifier = qp.get('identifier');
        if (method === 'otp' || method === 'password') {
            this.method.set(method as AuthMethod);
        }
        if (identifier === 'phone' || identifier === 'email') {
            this.identifier.set(identifier as IdentifierType);
        }
        this.resetTransientState();

        // If the user is already authenticated, redirect away from the login page.
        firstValueFrom(this.store.select(selectAuthStatus)).then((status) => {
            if (status?.isAuthenticated === true && status?.accessToken) {
                const claims = decodeJwtClaims(status.accessToken);
                if (claims && !isJwtExpired(claims)) {
                    const returnUrl = qp.get('returnUrl');
                    const dest = returnUrl && returnUrl.startsWith('/') && !this.isTrivialReturnUrl(returnUrl)
                        ? returnUrl
                        : this.defaultPostLoginUrl(status.accessToken);
                    this.router.navigateByUrl(dest).catch(() => { });
                }
            }
        }).catch(() => { });
    }

    async onOtpSubmit(): Promise<void> {
        this.message.set(null);
        this.error.set(null);

        const target =
            this.identifier() === 'phone'
                ? this.toE164(this.otpForm.value.mobile)
                : String(this.otpForm.value.email || '').trim();

        if (!target) {
            this.error.set(this.identifier() === 'phone' ? 'Enter a valid phone number.' : 'Enter a valid email.');
            return;
        }

	        if (this.otpStep() === 'target') {
	            this.loading.set(true);
	            if (this.identifier() === 'phone') {
	                this.authApi.sendOtp(target).subscribe({
	                    next: () => {
	                        this.loading.set(false);
	                        this.otpStep.set('code');
	                        this.message.set(`Code sent to ${target}`);
	                    },
	                    error: (err: any) => {
	                        this.loading.set(false);
	                        this.error.set(this.extractErrorMessage(err, 'Failed to send code'));
	                    }
	                });
	            } else {
	                this.authApi.sendEmailOtp(target).subscribe({
	                    next: () => {
	                        this.loading.set(false);
	                        this.otpStep.set('code');
	                        this.message.set(`Code sent to ${target}`);
	                    },
	                    error: (err: any) => {
	                        this.loading.set(false);
	                        this.error.set(this.extractErrorMessage(err, 'Failed to send code'));
	                    }
	                });
	            }
	            return;
	        }

        const code = String(this.otpForm.value.code || '').trim();
        if (!code) {
            this.error.set('Enter the code.');
            return;
        }

        this.loading.set(true);
        const verify$ = this.identifier() === 'phone' ? this.authApi.verifyOtp(target, code) : this.authApi.verifyEmailOtp(target, code);
        verify$.subscribe({
            next: async (session) => {
                this.loading.set(false);
                await this.onSession(session.access_token);
            },
            error: (err) => {
                this.loading.set(false);
                this.error.set(this.extractErrorMessage(err, 'Invalid code'));
            }
        });
    }

    resendOtp(): void {
        const target =
            this.identifier() === 'phone'
                ? this.toE164(this.otpForm.value.mobile)
                : String(this.otpForm.value.email || '').trim();

        if (!target) {
            this.error.set(this.identifier() === 'phone' ? 'Enter a valid phone number.' : 'Enter a valid email.');
            return;
        }

	        this.loading.set(true);
	        if (this.identifier() === 'phone') {
	            this.authApi.sendOtp(target).subscribe({
	                next: () => {
	                    this.loading.set(false);
	                    this.message.set(`Code resent to ${target}`);
	                },
	                error: (err: any) => {
	                    this.loading.set(false);
	                    this.error.set(this.extractErrorMessage(err, 'Failed to resend code'));
	                }
	            });
	        } else {
	            this.authApi.sendEmailOtp(target).subscribe({
	                next: () => {
	                    this.loading.set(false);
	                    this.message.set(`Code resent to ${target}`);
	                },
	                error: (err: any) => {
	                    this.loading.set(false);
	                    this.error.set(this.extractErrorMessage(err, 'Failed to resend code'));
	                }
	            });
	        }
	    }

    onPasswordSubmit(): void {
        this.message.set(null);
        this.error.set(null);

        const password = String(this.passwordForm.value.password || '');
        if (!password) {
            this.error.set('Enter your password.');
            return;
        }

        if (this.identifier() === 'phone') {
            const phoneE164 = this.toE164(this.passwordForm.value.mobile);
            if (!phoneE164) {
                this.error.set('Enter a valid phone number.');
                return;
            }

            this.loading.set(true);
            this.authApi.signInWithPhone(phoneE164, password).subscribe({
                next: async (session) => {
                    this.loading.set(false);
                    await this.onSession(session.access_token);
                },
                error: (err) => {
                    this.loading.set(false);
                    this.error.set(this.extractErrorMessage(err, 'Sign in failed'));
                }
            });
            return;
        }

        const email = String(this.passwordForm.value.email || '').trim();
        if (!email) {
            this.error.set('Enter a valid email.');
            return;
        }

        this.loading.set(true);
        this.authApi.signInWithEmail(email, password).subscribe({
            next: async (session) => {
                this.loading.set(false);
                await this.onSession(session.access_token);
            },
            error: (err) => {
                this.loading.set(false);
                this.error.set(this.extractErrorMessage(err, 'Sign in failed'));
            }
        });
    }

    private async onSession(accessToken: string): Promise<void> {
        this.store.dispatch(AuthActions.setAccessToken({ accessToken }));
        this.store.dispatch(AuthActions.checkAuthSuccess({ isAuthenticated: true }));
        this.store.dispatch(AuthActions.loadCurrentUser());

        const returnUrl = await this.resolveReturnUrl(accessToken);
        this.notifications.showSuccess('Signed in successfully.').subscribe();
        this.router.navigateByUrl(returnUrl).catch(() => {
            this.router.navigate(['/']).catch(() => { });
        });
    }

    private async resolveReturnUrl(accessToken: string): Promise<string> {
        const qp = await firstValueFrom(this.route.queryParamMap);
        const returnUrl = qp.get('returnUrl');
        const intent = String(qp.get('intent') || '').trim().toLowerCase();
        if (intent === 'reserve') {
            const fallback = returnUrl && returnUrl.startsWith('/') ? returnUrl : '/properties';
            const params = new URLSearchParams({
                verifyEmail: '1',
                verifyMobile: '1',
                verifyIdentity: '1',
                tab: 'documents',
                returnUrl: fallback
            });
            return `/profile?${params.toString()}`;
        }
        if (returnUrl && returnUrl.startsWith('/') && !this.isTrivialReturnUrl(returnUrl)) return returnUrl;
        return this.defaultPostLoginUrl(accessToken);
    }

    private isTrivialReturnUrl(url: string): boolean {
        const u = String(url || '').trim();
        return u === '/' || u.startsWith('/login') || u.startsWith('/signup') || u.startsWith('/forgot-password') || u.startsWith('/reset-password');
    }

    private defaultPostLoginUrl(accessToken: string): string {
        const claims = decodeJwtClaims(accessToken);
        const role = String(claims?.role || '').trim().toLowerCase();
        if (role === 'app_admin') return '/admin/dashboard';
        if (role === 'investor') return '/properties';
        if (role === 'subpartner') return '/developer/dashboard';
        if (role === 'sales') return '/developer/dashboard';
        return '/developer/dashboard';
    }

    private toE164(value: any): string | null {
        const countryCode = String(value?.countryCode || '').toUpperCase().trim();
        const rawPhone = String(value?.phone || '');
        const digits = rawPhone.replace(/[^\d]/g, '').replace(/^0+/, '');

        if (!countryCode || !digits) return null;
        const match = COUNTRIES.find((c) => c.code === countryCode);
        const dial = match?.dial_code || '';
        if (!dial || !dial.startsWith('+')) return null;

        return `${dial}${digits}`;
    }

    private extractErrorMessage(err: any, fallback: string): string {
        return err?.error?.error?.message || err?.error?.message || err?.message || fallback;
    }
}
